import React, { useEffect, useState, useCallback } from "react";
import { Radio, Trash2, Lock, Unlock, RefreshCw, Users, MessageSquare, Wifi } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../AuthContext";

export default function SupervisorPage() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [stats, setStats]       = useState(null);
  const [presence, setPresence] = useState({});
  const [loading, setLoading]   = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [chs, st, pr] = await Promise.all([
      api.getChannels(),
      api.getStats(),
      api.getPresence(),
    ]).catch(() => [[], null, {}]);
    setChannels(chs);
    setStats(st);
    setPresence(pr);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePrivate = async (ch) => {
    try {
      const updated = await api.updateChannel(ch.channel_id, { is_private: !ch.is_private });
      setChannels((prev) => prev.map((c) => c.channel_id === ch.channel_id ? updated : c));
    } catch {}
  };

  const deleteChannel = async (id) => {
    if (!window.confirm("Delete this channel? This cannot be undone.")) return;
    await api.deleteChannel(id);
    setChannels((prev) => prev.filter((c) => c.channel_id !== id));
  };

  if (!user || (user.role !== "admin" && user.role !== "employer")) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-gray-400">Supervisor access required.</p>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, color = "text-brand" }) => (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex items-center gap-4">
      <div className="p-2 rounded-lg bg-gray-800"><Icon className={`w-5 h-5 ${color}`} /></div>
      <div>
        <p className="text-2xl font-bold text-white">{value ?? "—"}</p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </div>
  );

  return (
    <div className="flex-1 p-6 overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-white flex items-center gap-2">
          <Radio className="w-5 h-5 text-brand" /> Supervisor Panel
        </h1>
        <button onClick={load}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-gray-800">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Radio}        label="Total Channels"  value={stats.total_channels} />
          <StatCard icon={Users}        label="Online Now"       value={stats.online_users} color="text-green-400" />
          <StatCard icon={Wifi}         label="Active Rooms"     value={stats.active_rooms} color="text-yellow-400" />
          <StatCard icon={MessageSquare} label="Total Messages" value={stats.total_messages} color="text-purple-400" />
        </div>
      )}

      {/* Channel Table */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-800">
          <h2 className="font-semibold text-white text-sm">All Channels</h2>
        </div>
        {loading ? (
          <p className="text-gray-500 text-sm p-4">Loading…</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-left">
                <th className="px-4 py-2.5 text-xs text-gray-500 font-medium">Name</th>
                <th className="px-4 py-2.5 text-xs text-gray-500 font-medium">Members</th>
                <th className="px-4 py-2.5 text-xs text-gray-500 font-medium">Online</th>
                <th className="px-4 py-2.5 text-xs text-gray-500 font-medium">Privacy</th>
                <th className="px-4 py-2.5 text-xs text-gray-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {channels.map((ch) => {
                const onlineCount = presence[ch.channel_id]?.length || 0;
                return (
                  <tr key={ch.channel_id} className="border-b border-gray-800 hover:bg-gray-800/40">
                    <td className="px-4 py-3">
                      <div className="font-medium text-white">{ch.name}</div>
                      {ch.description && <div className="text-xs text-gray-500">{ch.description}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-400">{ch.member_count || 0}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                        onlineCount > 0 ? "bg-green-500/20 text-green-400" : "bg-gray-700 text-gray-500"
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${onlineCount > 0 ? "bg-green-400" : "bg-gray-600"}`} />
                        {onlineCount}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => togglePrivate(ch)}
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-white"
                      >
                        {ch.is_private ? <Lock className="w-3.5 h-3.5 text-yellow-400" /> : <Unlock className="w-3.5 h-3.5" />}
                        {ch.is_private ? "Private" : "Public"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => deleteChannel(ch.channel_id)}
                        className="text-red-500 hover:text-red-400 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
